import { cssX } from "@datassential/platform-ui-lib";
import {
    RESPONDENTS_TYPES_CO_EXCLUSIVE_TYPES,
    RESPONDENTS_TYPES_SELECT_AMONG_GROUPS_COLUMNS,
    RESPONDENTS_TYPES_WITH_TOTAL_SELECT_AMONG_GROUPS_COLUMNS,
    RespondentType,
    RespondentTypeGroup
} from "@models/RespondentType";
import cn from "classnames";
import { containsSome, toggleInArray } from "@core/utils/array";
import { Toggle, ToggleType } from "@components/Toggle";
import { useRespondentTypesGroups } from "@core/CommonDataContext";
import { SelectAmongData } from "@vms/SelectAmong/SelectAmongContext";
import { FC, useMemo } from "react";

export const SelectAmongRespondentTypesItem: FC<{
    id: RespondentType;
    name: string;
    value: RespondentType[];
    onChange: () => void;
    testId?: string;
    groupId?: RespondentTypeGroup;
    isGroupsLimitReached: boolean;
    useCoExclusions?: boolean;
}> = ({
    id,
    name,
    value,
    onChange,
    testId,
    groupId,
    isGroupsLimitReached,
    useCoExclusions,
}) => {
    const originalRespondentTypesGroups = useRespondentTypesGroups();
    const respondentTypesGroups = useRespondentTypesGroups({
        isEatingHabitsExtended: true,
    });

    const isDisabledByCoExclusive = useMemo(
        () => {
            if (!useCoExclusions) {
                return false;
            }

            const coExclusiveTypes = RESPONDENTS_TYPES_CO_EXCLUSIVE_TYPES.find(
                g => g.some(gg => gg.includes(id))
            );


            if (coExclusiveTypes) {
                const otherTypes = coExclusiveTypes.filter(g => !g.includes(id));

                return otherTypes.some(g => containsSome(value, g));
            }

            return false;
        },
        [id, value, useCoExclusions]
    );

    const groupItemsIds = useMemo(
        () => respondentTypesGroups
            .find(
                g => g.id === groupId
            )
            ?.items.map(i => i.id as RespondentType) ?? [],
        [groupId, respondentTypesGroups]
    );

    const originalGroupItemsIds = useMemo(
        () => originalRespondentTypesGroups
            .find(
                g => g.items.some(i => i.id === id)
            )
            ?.items.map(i => i.id as RespondentType) ?? [],
        [id, originalRespondentTypesGroups]
    );

    const isGroupComposite = useMemo(
        () => {
            return groupItemsIds.length !== originalGroupItemsIds.length;
        },
        [groupItemsIds, originalGroupItemsIds]
    );

    const isDisabledByCompositeGroupAndLimit = useMemo(
        () => {
            if (isGroupsLimitReached && isGroupComposite) {
                return !containsSome(originalGroupItemsIds, value);
            }

            return false;
        },
        [isGroupsLimitReached, isGroupComposite, originalGroupItemsIds, value]
    );

    return (
        <div className={cn(
            cssX("Dropdown__row"),
            "SelectAmong__disable-element",
            { 'is-disabled': isDisabledByCoExclusive || isDisabledByCompositeGroupAndLimit },
        )}>
            <div
                className={cn(
                    cssX("Dropdown__item"),
                    'SelectAmong__item',
                )}
                data-testid={testId}
                onClick={onChange}
            >
                <Toggle
                    type={ToggleType.Checkbox}
                    isChecked={value.includes(id)}
                    onChange={() => null}
                    label={name}
                />
            </div>
        </div>
    );
};

const SelectAmongRespondentTypes: FC<{
    value: RespondentType[];
    dispatchPartial: (partialData: Partial<SelectAmongData>) => void;
    isGroupsLimitReached: boolean;
    showTotalOption?: boolean;
    useCoExclusions?: boolean;
}> = ({
    value,
    dispatchPartial,
    isGroupsLimitReached,
    showTotalOption = false,
    useCoExclusions = false,
}) => {
    const respondentTypesGroups = useRespondentTypesGroups({
        isEatingHabitsExtended: true,
    });

    const selectedGroupsIds = useMemo(
        () => respondentTypesGroups
            .filter(g => g.items.some(
                i => value.includes(i.id as RespondentType)
            ))
            .map(g => g.id),
        [respondentTypesGroups, value]
    );

    const columns = useMemo(
        () => {
            if (showTotalOption) {
                return RESPONDENTS_TYPES_WITH_TOTAL_SELECT_AMONG_GROUPS_COLUMNS;
            }

            return RESPONDENTS_TYPES_SELECT_AMONG_GROUPS_COLUMNS;
        },
        [showTotalOption]
    );

    return (<>
        {columns.map((col, index) => {
            const id = index;
            const groups = respondentTypesGroups.filter(
                (group) => col.includes(group.id ?? RespondentTypeGroup.NoData)
            );

            return (
                <div
                    className="SelectAmong__col"
                    key={id}
                >
                    {groups.map((group, groupIndex) => (
                        <div
                            key={group.id}
                            className={cn(
                                cssX("Dropdown__group"),
                                "SelectAmong__disable-element",
                                {
                                    'is-disabled': isGroupsLimitReached && !selectedGroupsIds.includes(group.id),
                                }
                            )}
                        >
                            {!!group.name && (
                                <div
                                    className={cn(
                                        cssX("Dropdown__group-title"),
                                        "SelectAmong__group-title",
                                    )}
                                    data-testid={`textRespondentGroupTitle${index}_${groupIndex}`}
                                >
                                    {group.name}
                                </div>
                            )}
                            <div className={cssX("Dropdown__group-content")}>
                                {group.items.map((item, itemIndex) => (

                                    <SelectAmongRespondentTypesItem
                                        key={item.id}
                                        id={item.id as RespondentType}
                                        name={item.name ?? ''}
                                        value={value}
                                        onChange={() => dispatchPartial({
                                            respondentTypes: toggleInArray(
                                                value,
                                                item.id as RespondentType
                                            ),
                                        })}
                                        testId={`buttonRespondentTypeItem${index}_${groupIndex}_${itemIndex}`}
                                        groupId={group.id}
                                        isGroupsLimitReached={isGroupsLimitReached}
                                        useCoExclusions={useCoExclusions}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            );
        })}
    </>);

};

export default SelectAmongRespondentTypes;